import React, {useEffect, useState} from 'react'
import {isAndroid, isIOS} from 'react-device-detect'
import {FormLayout1} from '~/components/Primitives/Forms/FormLayout1'
import {Button} from 'nf-ui'
import {Column, Row} from '~/components/Primitives/Layout'
import {PageLoading} from '~/components/PageLoading'

export const SuggestMobile: React.FC<{dismissed: boolean; setDismissed: (value: boolean) => void}> = ({
    dismissed,
    setDismissed,
    children,
}) => {
    const [imagesLoaded, setImagesLoaded] = useState(false)
    const [viewportWidth, setViewportWidth] = useState<number | null>(null)

    const showAndroid = isAndroid
    const showIOS = isIOS

    // Get accurate viewport width, accounting for mobile browser initialization issues
    useEffect(() => {
        const getViewportWidth = () => {
            // Use visualViewport for iOS (more accurate), fallback to innerWidth
            if (isIOS && window.visualViewport) {
                return window.visualViewport.width
            }
            return window.innerWidth
        }

        // Set initial width
        setViewportWidth(getViewportWidth())

        // Listen for viewport changes (handles mobile browser viewport adjustment and orientation changes)
        const handleResize = () => {
            setViewportWidth(getViewportWidth())
        }

        // Handle orientation changes specifically (some browsers need this for reliable detection)
        let orientationTimeoutId: NodeJS.Timeout | null = null
        const handleOrientationChange = () => {
            // Clear any pending timeout
            if (orientationTimeoutId) {
                clearTimeout(orientationTimeoutId)
            }
            // Small delay to ensure viewport has settled after orientation change
            orientationTimeoutId = setTimeout(handleResize, 100)
        }

        // Use visualViewport resize event if available (more reliable on mobile)
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleResize)
        }
        window.addEventListener('resize', handleResize)
        // Listen for orientation changes explicitly
        window.addEventListener('orientationchange', handleOrientationChange)
        // Also listen for screen orientation API if available (more modern approach)
        if (window.screen?.orientation) {
            window.screen.orientation.addEventListener('change', handleOrientationChange)
        }

        // Also check after a short delay to catch delayed viewport adjustments
        const timeoutId = setTimeout(handleResize, 100)

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleResize)
            }
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('orientationchange', handleOrientationChange)
            if (window.screen?.orientation) {
                window.screen.orientation.removeEventListener('change', handleOrientationChange)
            }
            clearTimeout(timeoutId)
            if (orientationTimeoutId) {
                clearTimeout(orientationTimeoutId)
            }
        }
    }, [])

    // Determine if we should show the mobile prompt
    const shouldShowPrompt = viewportWidth !== null && !dismissed && viewportWidth <= 809 && (showAndroid || showIOS)

    // Always render children in the same position to preserve component mounting
    // Hide children when showing prompt to maintain original UX behavior
    return (
        <>
            {shouldShowPrompt ? (
                <div>
                    {!imagesLoaded ? <PageLoading></PageLoading> : null}
                    <div style={imagesLoaded ? {} : {display: 'none'}}>
                        <FormLayout1
                            heading=""
                            subHeading={[
                                {text: `You're on a mobile screen`, bold: true, centerHorizontal: true},
                                {
                                    text: (
                                        <>
                                            Creating and managing your directory is best done from a desktop browser.
                                            Please sign in on a larger screen at{' '}
                                            <a href="https://namesandfaces.com">namesandfaces.com</a>
                                        </>
                                    ),
                                    bold: false,
                                    centerHorizontal: true,
                                },
                                {text: ` `},
                                {text: ` `},
                                {text: `Download the Mobile App`, bold: true, centerHorizontal: true},
                                {
                                    text: `View your directory using the Names & Faces Mobile App for the best experience.`,
                                    bold: false,
                                    centerHorizontal: true,
                                },
                            ]}
                            progress={0}
                        >
                            <Column width="100%">
                                <Row centerHorizontal={true}>
                                    {showIOS ? (
                                        <a href="https://apps.apple.com/us/app/names-faces/id949875464">
                                            <img
                                                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                                                alt="Download on the App Store"
                                                style={{width: '177px'}}
                                                onLoad={() => setImagesLoaded(true)}
                                            ></img>
                                        </a>
                                    ) : null}
                                    {showAndroid ? (
                                        <a href="http://play.google.com/store/apps/details?id=za.co.polymorph.namesfaces">
                                            <img
                                                width="250"
                                                src="https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png"
                                                alt="Get it on Google Play"
                                                onLoad={() => setImagesLoaded(true)}
                                            ></img>
                                        </a>
                                    ) : null}
                                </Row>
                            </Column>
                        </FormLayout1>
                    </div>
                </div>
            ) : null}
            <div style={{
                height: '100%',
                ...(shouldShowPrompt ? {display: 'none'} : {})
            }}>{children}</div>
        </>
    )
}
